import React, { FC, useContext, useEffect } from 'react';
import _ from 'lodash';
import { getMinBound, formatNumWithKMBAlt, getMaxBound, scaleMaxValue, scaleMinValue } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
import { Spin } from 'antd';
import useTrackTabChange from 'hooks/general/useTrackTabChange';

if (typeof window !== 'undefined') {
    var G2 = require('@antv/g2');
    var { Chart } = G2;
}

interface Props {
    dataset: Array<any>;
    chartContainer: string;
    padding?: [number, number, number, number];
    height: number;
    width?: number;
    fields?: Array<string>;
    labelConfiguration?: boolean;
    k?: boolean;
    yAxisTitle?: string;
    xAsisTitle?: string;
    bubbleSizeTitle?: string;
    alter?: boolean;
    lineColor?: string;
    colorList?: string[];
    fill?: string;
    stroke?: string;
    loading?: boolean;
    error?: any;
    xRange?: any;
    yRange?: any;
    meanLineChart?: any;
    showQuadrant?: boolean;
    mixpanelTrackLegend?: Function;
    xAxisPercentage?: boolean;
    yAxisPercentage?: boolean;
    colorFieldOrder?: string[];
}

const BubbleChart: FC<Props> = ({
    dataset,
    chartContainer,
    height,
    width,
    fields = ['investments', 'roi', 'tactic', 'incremental_retailer_profit_per_week', 'investments'],
    labelConfiguration = true,
    xAsisTitle,
    yAxisTitle,
    bubbleSizeTitle = '',
    alter = false,
    lineColor,
    colorList,
    fill,
    stroke,
    loading,
    showQuadrant = false,
    error,
    xRange = { xMin: 0, xMax: 800000 },
    yRange = { xMin: 0, xMax: 8 },
    meanLineChart = false,
    mixpanelTrackLegend,
    xAxisPercentage = false,
    yAxisPercentage = false,
    colorFieldOrder,
}) => {
    const isWindowTabActive = useTrackTabChange();

    const { appConfig } = useContext(UserContext);
    const commonSetting = getConfig('commonConfig', appConfig);
    const opportunityIdentificationConfig = getConfig('opportunityIdentification', appConfig);
    const { decimalForNonPercentage, decimalForPercentage } = commonSetting;

    const { graphFontSize, graphLabelColor, units, graphFontSizeAlt, graphLabelColorAlt, graphFontFamily } =
        commonSetting;
    // if (!loading && error && dataset?.length === 0) {
    //     return <ErrorBox msg={String(error)} />;
    // }
    useEffect(() => {
        if (!window) return;
        const defaultTooltipConfig = {
            showMarkers: false,
            shared: true,
            showCrosshairs: false,
            follow: true,
        };

        const chart = new Chart({
            container: chartContainer,
            autoFit: true,
            height: height,
            appendPadding: [25, 10, 0, 0],
        });
        chart.data(dataset);
        const minFields1: any = getMinBound(dataset, [fields[0]]);
        const minFields2: any = getMinBound(dataset, [fields[1]]);
        const maxFields1: any = getMaxBound(dataset, [fields[0]]);
        const maxFields2: any = getMaxBound(dataset, [fields[1]]);
        const isXAxisBpe = fields[0] === 'bpe'; // if x axis is bpe, show x axis in descending order
        const yAxisAlter = fields[1] === 'ppe' && opportunityIdentificationConfig?.reversePpeAxis; // if x axis is bpe, show x axis in descending order
        // [fields[0]]: {
        //     nice: true,
        //     min: scaleMinValue(10, minFields1), //scaling so that the data doesnt appear near the origin
        //     max: dataset?.length === 1 ? scaleMaxValue(10, maxFields1) : null, //scaling so that the data doesnt appear near the origin
        //     range: [0, 0.95],
        //     // min: minFields1,
        //     //// max: xRange.xMax,
        // },
        // [fields[1]]: {
        //     nice: true,
        //     min: scaleMinValue(10, minFields2), //giving a min value to avoid overflow of points near to axis values
        //     max: dataset?.length === 1 ? scaleMaxValue(10, maxFields2) : null,
        //     range: [0, 0.95],
        //     // min: minFields2,
        //     //// max: yRange.xMax,
        // },
        chart.scale({
            [fields[0]]: {
                nice: true,
                min: dataset?.length === 1 ? scaleMinValue(10, minFields1) : null, //scaling so that the data doesnt appear near the origin
                max: dataset?.length === 1 ? scaleMaxValue(10, maxFields1) : null, //scaling so that the data doesnt appear near the origin
                range: [0.05, 0.95],
            },
            [fields[1]]: {
                nice: true,
                min: dataset?.length === 1 ? scaleMinValue(10, minFields2) : null, //giving a min value to avoid overflow of points near to axis values
                max: dataset?.length === 1 ? scaleMaxValue(10, maxFields2) : null,
                range: [0.05, 0.95],
            },
            // Explicitly set the color field category order so that each tactic
            // always maps to the same color index, even when data points change
            ...(colorFieldOrder && colorFieldOrder.length > 0
                ? {
                      [fields[2]]: {
                          type: 'cat',
                          values: colorFieldOrder,
                      },
                  }
                : {}),
        });
        chart.tooltip({
            ...defaultTooltipConfig,
            containerTpl:
                '<div class="g2-tooltip" style="min-width:250px;">' +
                '<div class="g2-tooltip-title" style="margin:10px 0;"></div>' +
                '<ul class="g2-tooltip-list"></ul></div>',
        });
        chart.axis(fields[0], {
            title: {
                text: xAsisTitle,
                style: {
                    fontSize: graphFontSize,
                    fontFamily: commonSetting.graphFontFamily,
                    fill: graphLabelColor,
                    fontWeight: '500',
                },
            },
            label: {
                formatter: (val: any) => {
                    if (xAxisPercentage) {
                        return formatNumWithKMBAlt(val, decimalForPercentage) + '%';
                    }
                    return `${
                        (isXAxisBpe && val != 0 ? '-' : '') +
                        formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage)
                    }`;
                },
                style: {
                    fontSize: graphFontSize,
                    fontFamily: commonSetting.graphFontFamily,
                    fill: graphLabelColor,
                },
            },
            grid: {
                line: {
                    style: {
                        stroke: stroke,
                        lineWidth: 1,
                        lineDash: [2, 2],
                    },
                },
            },
        });
        chart.axis(fields[1], {
            // title: null,
            title: {
                text: yAxisTitle,
                style: {
                    fontSize: graphFontSize,
                    fontFamily: commonSetting.graphFontFamily,
                    fill: graphLabelColor,
                    fontWeight: '500',
                },
            },
            label: {
                formatter: (val: any) => {
                    if (yAxisPercentage) {
                        return formatNumWithKMBAlt(val, decimalForPercentage) + '%';
                    }
                    return alter
                        ? formatNumWithKMBAlt(val, decimalForPercentage) + '%'
                        : (yAxisAlter && val != 0 ? '-' : '') +
                              formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                },
                style: {
                    fontSize: graphFontSize,
                    fontFamily: commonSetting.graphFontFamily,
                    fill: graphLabelColor,
                },
            },
            grid: {
                line: {
                    style: {
                        stroke: stroke,
                        lineWidth: 1,
                        lineDash: [2, 2],
                    },
                },
            },
        });
        // chart.tooltip(false);
        // chart.legend(false);

        //for tooltip x axis
        chart
            .point()
            .position(`${fields[0]}*${fields[1]}`)
            .size(fields[3], [10, 40])
            //.size(0)
            .color('transparent')
            //.shape('circle')
            .tooltip(`name*${fields[0]}`, (name: string, xAxis: any) => {
                //  console.log(xAxis, yAxis, 'logging g');
                return {
                    title: name,
                    name: `X Axis: ${xAsisTitle}`,
                    value: xAxisPercentage
                        ? `${formatNumWithKMBAlt(xAxis, decimalForPercentage)}%`
                        : `${
                              (isXAxisBpe ? '-' : '') +
                              formatNumWithKMBAlt(xAxis, decimalForNonPercentage, decimalForNonPercentage)
                          }`,
                };
            });
        //for tooltip y axis
        chart
            .point()
            .position(`${fields[0]}*${fields[1]}`)
            .size(fields[3], [10, 40])
            //.size(0)
            .color('transparent')
            //.shape('circle')
            .tooltip(`name*${fields[1]}`, (name: string, yAxis: any) => {
                //  console.log(xAxis, yAxis, 'logging g');
                return {
                    title: name,
                    name: `Y Axis: ${yAxisTitle}`,
                    value: yAxisPercentage
                        ? `${formatNumWithKMBAlt(yAxis, decimalForPercentage)}%`
                        : `${
                              (yAxisAlter ? '-' : '') +
                              formatNumWithKMBAlt(yAxis, decimalForNonPercentage, decimalForNonPercentage)
                          }`,
                };
            });

        //for tooltip bubble size
        chart
            .point()
            .position(`${fields[0]}*${fields[1]}`)
            .size(fields[3], [10, 40])
            //.size(0)
            .color('transparent')
            //.shape('circle')
            .tooltip(`name*${fields[3]}`, (name: string, size: any) => {
                //  console.log(xAxis, yAxis, 'logging g');
                return {
                    title: name,
                    name: `Bubble Size: ${bubbleSizeTitle || _.startCase(fields[3])}`,
                    value: `${formatNumWithKMBAlt(size, decimalForNonPercentage, decimalForNonPercentage)}`,
                };
            });

        //  if (labelConfiguration) {
        chart
            .point()
            .position(`${fields[0]}*${fields[1]}`)
            .size(fields[3], [10, 40])
            .color(fields[2], colorList)
            .shape('circle')
            .style(fields[2], (val: any) => {
                return {
                    lineWidth: 1,
                    strokeOpacity: 1,
                    fillOpacity: 0.8,
                    // val: formatNumWithKMBAlt(val, 2, 2),
                };
            })
            .label(fields[5], (val: number) => {
                if (!labelConfiguration) return;
                return {
                    offsetY: 12,
                    content: val,
                    style: {
                        fontWeight: 500,
                        fontSize: graphFontSizeAlt,
                        fill: graphLabelColorAlt,
                        fontFamily: graphFontFamily,
                    },
                };
            })
            .tooltip(`name*${fields[2]}`, (name: string, color: any) => {
                //  console.log(xAxis, yAxis, 'logging g');
                return {
                    title: name,
                    name: `Bubble Color: `,
                    value: _.startCase(color),
                };
            });
        //  } else {
        // chart
        //     .point()
        //     .position(`${fields[0]}*${fields[1]}`)
        //     .size(fields[3], [10, 40])
        //     .color(fields[2], colorList)
        //     .shape('circle')
        //     .style(fields[2], (val: any) => {
        //         return {
        //             lineWidth: 1,
        //             strokeOpacity: 1,
        //             val: formatNumWithKMBAlt(val, 2, 2),
        //         };
        //     })
        //     .label(labelConfiguration)
        //     .tooltip(`name*${fields[2]}`, (name: string, color: any) => {
        //         //  console.log(xAxis, yAxis, 'logging g');
        //         return {
        //             title: name,
        //             name: `Bubble Color: ${_.startCase(fields[2])}`,
        //             value: _.startCase(color),
        //         };
        //     });
        //    }

        //select a region to zoom
        G2.registerInteraction('brush', {
            showEnable: [
                { trigger: 'plot:mouseenter', action: 'cursor:crosshair' },
                { trigger: 'plot:mouseleave', action: 'cursor:default' },
            ],
            start: [
                {
                    trigger: 'plot:mousedown',
                    action: ['brush:start', 'rect-mask:start', 'rect-mask:show'],
                },
            ],
            processing: [
                {
                    trigger: 'plot:mousemove',
                    action: ['rect-mask:resize'],
                },
            ],
            end: [
                {
                    trigger: 'plot:mouseup',
                    action: ['brush:filter', 'brush:end', 'rect-mask:end', 'rect-mask:hide'],
                },
            ],
            rollback: [{ trigger: 'dblclick', action: ['brush:reset'] }],
        });
        chart.interaction('brush');

        //sorting the data for slider
        //if its bpe, order in descending since we are taking absolute
        if (isXAxisBpe) {
            dataset?.sort((a, b) => a[fields[0]] - b[fields[0]]);
        } else dataset?.sort((a, b) => a[fields[0]] - b[fields[0]]);
        chart.data(dataset).option('slider', {
            formatter: (val: number, datum: any, idx: any) => {
                if (!datum) return '';
                if (xAxisPercentage) {
                    return formatNumWithKMBAlt(datum[fields[0]], decimalForPercentage) + '%';
                }
                return (
                    (isXAxisBpe ? '-' : '') +
                    formatNumWithKMBAlt(datum[fields[0]], decimalForNonPercentage, decimalForNonPercentage)
                );
            },
            textStyle: {
                fontSize: graphFontSize,
                fontFamily: graphFontFamily,
                fill: graphLabelColor,
                fontWeight: '500',
            },
            handlerStyle: {
                label: {
                    style: {
                        fontSize: graphFontSize,
                        fontFamily: graphFontFamily,
                        fill: graphLabelColor,
                        fontWeight: '600',
                    },
                },
                style: {
                    fontSize: graphFontSize,
                    fontFamily: graphFontFamily,
                    fill: graphLabelColor,
                    fontWeight: '500',
                },
            },

            trendCfg: {
                data: [2, 1, 0],
                isArea: false,
                smooth: true,
                lineStyle: {
                    fill: 'transparent',
                },
            },
        });

        chart.legend('tactics', {
            position: 'top',
            padding: [0, 0, 25, 0],
            offsetY: 15,
            itemName: {
                style: {
                    fontSize: graphFontSize,
                },
            },
            pageNavigator: {
                marker: {
                    style: {
                        inactiveFill: graphLabelColorAlt,
                        inactiveOpacity: 0.35,
                        fill: graphLabelColorAlt,
                        opacity: 1,
                        size: graphFontSize - 3,
                    },
                },
                text: {
                    style: {
                        fill: graphLabelColorAlt,
                        fontSize: graphFontSize,
                    },
                },
            },
        });
        // chart.legend(`${fields[3]}`, false);
        chart.legend(`${fields[3]}`, {
            position: 'top-right',
            offsetY: 15,
            label: {
                formatter: (val: number) => {
                    return formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                },
                style: {
                    fontSize: graphFontSize,
                    fontFamily: graphFontFamily,
                    fill: graphLabelColor,
                    fontWeight: '500',
                },
            },
            title: {
                text: 'Bubble Size Filter', //adding space to make it center aligned for now
                spacing: 10,
                align: 'bottom',
                style: {
                    fontSize: graphFontSize,
                    fontFamily: graphFontFamily,
                    fill: graphLabelColor,
                    fontWeight: '500',
                },
            },
        });

        if (showQuadrant) {
            const totalWeight = _.sumBy(dataset, (d: any) => d?.[fields[3]] || 0);
            if (meanLineChart && totalWeight > 0) {
                const mean = (valueField: string) =>
                    _.sumBy(dataset, (d: any) => (d?.[valueField] || 0) * (d?.[fields[3]] || 0)) / totalWeight;
                const xMean = mean(fields[0]);
                const yMean = mean(fields[1]);
                const style = { stroke: lineColor, lineWidth: 2 };
                chart.annotation().line({ start: [xMean, 'min'], end: [xMean, 'max'], style });
                chart.annotation().line({ start: ['min', yMean], end: ['max', yMean], style });
            } else {
                chart.line().position(`${fields[5]}*0`).color(lineColor);
                chart.line().position(`0*${fields[2]}`).color(lineColor);
            }
        }
        chart.axis(fields[2], false);
        chart.axis(fields[3], false);
        chart.axis(fields[4], false);

        if (mixpanelTrackLegend) {
            //for mixpanel
            chart.on('legend:mousedown', (...args: any) => {
                const text = args?.[0]?.gEvent?.target?.attrs?.text;
                mixpanelTrackLegend(text);
            });
        }

        if (window) {
            try {
                chart.render();
            } catch (e) {
                console.log('This is chart render block error', e);
            }
        }
        return () => {
            chart.destroy();
        };
    }, [dataset, labelConfiguration, fields, isWindowTabActive, xAxisPercentage, yAxisPercentage, colorFieldOrder]);

    return (
        <div className="">
            {loading ? (
                <div className="flex justify-center items-center w-full h-96 ">
                    <Spin />
                </div>
            ) : (
                <div id={chartContainer} />
            )}
        </div>
    );
};

function arePropsEqual(prevProps: any, nextProps: any) {
    return _.isEqual(prevProps, nextProps);
}

export default React.memo(BubbleChart, arePropsEqual);
